import axios from "axios";
import axiosRetry from "axios-retry";
import { ResponseVehicle } from "services/motorpoint/shortlist";
import axiosOnSuccessEvent from "utils/axiosOnSuccessEvent";
import { logError } from "utils/logger";

import { VehicleService } from "./vehicle";

export type VehicleMakesReturnType = { makes: string[] };

export type VehicleModelsReturnType = {
    models: { label: string; value: string; category: string }[];
    noModels: string[];
};

export type VehicleStockCountReturnType = {
    count: number;
};

export type VehicleMinMaxPriceReturnType = {
    price: {
        monthly: {
            min: number;
            max: number;
        };
        total: {
            min: number;
            max: number;
        };
    };
};

//copied from vehicle-api/lib/vehicle-api-stack.ts
const routes = {
    stockBookNumber: "{stockBookNumber}",
    stockBookNumberMinified: "{stockBookNumber}/minified",
    ukttStockBookNumber: "{stockBookNumber}/uktt",
    makes: "search-magnet/makes",
    models: "search-magnet/models",
    cheapest: "cheapest",
    minMaxPrice: "search-magnet/prices",
    count: "search-magnet/counts",
};

export const vehicleService = () => {
    const getVehicleByStockNumber = async (
        stockNumber: string | number
    ): Promise<VehicleService.Vehicle | undefined> => {
        if (!stockNumber) return;
        try {
            const response = await axios.get<VehicleService.Vehicle>(
                `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.stockBookNumber.replace("{stockBookNumber}", `${stockNumber}`)}`,
                {
                    headers: {
                        "x-api-key": process.env.VEHICLE_API_KEY,
                        "Accept-Encoding": "gzip, deflate, br",
                        Accept: "application/json",
                    },
                }
            );

            axiosOnSuccessEvent(response);

            return response.data;
        } catch (e) {
            const error = e as { message: string; msg: string };
            logError("Error from vehicle request (getVehicleByStockNumber)", error.msg);
            return undefined;
        }
    };

    const getMinifiedVehicleByStockNumber = async (
        stockNumber: string | number
    ): Promise<ResponseVehicle | undefined> => {
        if (!stockNumber) return;
        try {
            const response = await axios.get<ResponseVehicle>(
                `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.stockBookNumberMinified.replace("{stockBookNumber}", `${stockNumber}`)}`,
                {
                    headers: {
                        "Accept-Encoding": "gzip, deflate, br",
                        Accept: "application/json",
                    },
                }
            );

            axiosOnSuccessEvent(response);

            return response.data;
        } catch (e) {
            const error = e as { message: string; msg: string };
            logError("Error from vehicle request (getMinifiedVehicleByStockNumber)", error.msg);
            return undefined;
        }
    };

    const getCheapestVehiclesByMakeAndModel = async (
        params: { make: string; model: string }[]
    ): Promise<ResponseVehicle[] | undefined> => {
        if (!Array.isArray(params) || !params.length) return;

        try {
            axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });
            const response = await axios.post<ResponseVehicle[]>(
                `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.cheapest}`,
                params,
                {
                    headers: {
                        "Accept-Encoding": "gzip, deflate, br",
                        Accept: "application/json",
                    },
                }
            );

            axiosOnSuccessEvent(response);

            return response.data;
        } catch (e) {
            const error = e as { message: string; msg: string };
            logError("Error from vehicle request (getCheapestVehiclesByMakeAndModel)", {
                msg: error.msg,
                params,
            });
            return undefined;
        }
    };

    const fetchData = async <T>(params: Record<string, unknown>, url: string) => {
        try {
            axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });
            const response = await axios.post<T>(url, params, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            axiosOnSuccessEvent(response);

            return response.data;
        } catch (e) {
            const error = e as { message: string; msg: string };
            logError(`Error from vehicle request (${url})`, {
                msg: error.msg,
                params,
            });
            return undefined;
        }
    };

    const getVehicleMakes = async (params: {
        commercial: boolean;
    }): Promise<VehicleMakesReturnType | undefined> => {
        return await fetchData<VehicleMakesReturnType>(
            params,
            `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.makes}`
        );
    };

    const getVehicleModels = async (params: {
        commercial: boolean;
        makes: string[];
    }): Promise<VehicleModelsReturnType | undefined> => {
        return await fetchData(
            params,
            `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.models}`
        );
    };

    const getVehicleStockCount = async (params: {
        commercial: boolean;
    }): Promise<VehicleStockCountReturnType | undefined> => {
        return await fetchData(
            params,
            `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.count}`
        );
    };

    const getMinMaxPrices = async (params: {
        commercial: boolean;
    }): Promise<VehicleMinMaxPriceReturnType | undefined> => {
        return await fetchData(
            params,
            `${process.env.NEXT_PUBLIC_VEHICLE_API_URL}/${routes.minMaxPrice}`
        );
    };

    return {
        getVehicleByStockNumber,
        getMinifiedVehicleByStockNumber,
        getCheapestVehiclesByMakeAndModel,
        getVehicleMakes,
        getVehicleModels,
        getVehicleStockCount,
        getMinMaxPrices,
    };
};
